<template>
    <v-container class="pa-4">
        <v-row
            v-if="getTicketImages.length > 0"
            class="ticket-images-container"
        >
            <v-col
                v-if="!$vuetify.breakpoint.xs"
                class="upload-img-btn col-9"
            >
                <v-btn
                    class="text-subtitle-2"
                    style="text-transform: none !important"
                    outlined
                    light
                    @click="showImageUploadDialog=true"
                >
                    <v-icon
                        class="material-icons-outlined"
                    >
                        upload
                    </v-icon>
                    Upload
                </v-btn>
                <v-btn
                    v-if="(!offline && offlineImageCount)"
                    class="text-subtitle-2 ml-2"
                    style="text-transform: none !important"
                    color="#FFEFEE"
                    elevation="0"
                    @click="uploadOfflineImages"
                >
                    <v-icon
                        class="material-icons-outlined"
                    >
                        upload
                    </v-icon>
                    Upload Offline Images
                </v-btn>                
            </v-col>
            <v-col
                v-if="$vuetify.breakpoint.xs"
                col-3
                class="mobile-view-selectall"
            >
                <v-checkbox
                    v-model="selectAll"
                    @change="selectAllItems"
                />Select All
            </v-col>
            <v-col>
                <ImageDeleteDialog
                    :use-header-button="true"
                    :images="selectedImages"
                    :selected-images-name="selectedImagesName"
                    :rowids="selectedImages"
                    @load-ticket-details="loadTicketDetails"
                />
            </v-col>
        </v-row>
        <v-row
            v-if="getTicketImages.length > 0 && !$vuetify.breakpoint.xs"
            class="mt-2"
        >
            <v-col>
                <v-data-table
                    id="main_images_table"
                    ref="main_images_table"
                    v-model="selectedImages"
                    fixed-header
                    show-select
                    :headers="headers"
                    :items="getTicketImages"
                    :items-per-page="getTicketImages.length"
                    :item-class="itemRowColor"
                    item-key="rowid"
                    hide-default-footer                        
                >
                    <template
                        v-slot:header.data-table-select="{ on, props }"
                    >
                        <v-simple-checkbox
                            v-ripple
                            v-bind="props"
                            on-icon="check_box"
                            indeterminate-icon="indeterminate_check_box"
                            off-icon="check_box_outline_blank"
                            :disabled="offlineSelectAllDisabled"
                            v-on="on"
                        />
                    </template>
                    <template
                        v-slot:item.data-table-select="{ item, isSelected, select }"
                    >
                        <v-simple-checkbox
                            v-ripple
                            on-icon="check_box"
                            off-icon="check_box_outline_blank"
                            :value="isSelected"
                            :disabled="(offline && !item.offline)"
                            @input="select($event)"
                        />
                    </template>

                    <template v-slot:[`item.view`]="{item}">
                        <v-icon
                            class="cp"
                            :disabled="offline && !item.offline"
                            @click="showPreviewImage(item)"
                        >
                            preview
                        </v-icon>
                    </template>
                    <template v-slot:[`item.edit`]="{item}">
                        <ImageEditDialog
                            :row="item"
                            :disabled="offline && !item.offline"
                            @load-ticket-details="loadTicketDetails"
                        />
                    </template>
                    <template v-slot:[`item.delete`]="{item}">
                        <ImageDeleteDialog 
                            :use-header-button="false"
                            :images="[item]"
                            :image-caption="item.caption"
                            :rowid="item.rowid"
                            :single-select-disabled="offline && !item.offline"
                            @load-ticket-details="loadTicketDetails"
                        />
                    </template>
                </v-data-table>
            </v-col>
        </v-row>

        <div :class="{'image-cards': $vuetify.breakpoint.xs}">
            <div
                v-if="getTicketImages.length > 0 && $vuetify.breakpoint.xs"
                id="ticket-images-cards"
                class="mb-15"
            >
                <v-card
                    v-for="item in getTicketImages"
                    :key="item.rowid"
                    flat
                    tile
                >
                    <v-toolbar
                        flat
                        class="ticket-card-header px-3"
                    >
                        <div class="checkbox-container">
                            <v-checkbox
                                v-model="selectedImages"
                                :value="item.rowid"
                                @change="handelSelectItem"
                            />
                        </div>
                        <b>{{ item.caption }}</b>
                        <v-spacer />
                        <v-icon
                            :class="{'mr-4': $vuetify.breakpoint.xs, 'mr-2': !$vuetify.breakpoint.xs}"
                            @click="showPreviewImage(item)"
                        >
                            preview
                        </v-icon>
                        <ImageEditDialog
                            :row="item"
                            :disabled="offline && !item.offline"
                            @load-ticket-details="loadTicketDetails"
                        />
                        <span>&nbsp;</span>
                        <ImageDeleteDialog
                            :use-header-button="false"
                            :images="[item]"
                            :disabled="offline && !item.offline"
                            :image-caption="item.caption" 
                            :rowid="item.rowid" 
                            :single-select-disabled="offline && !item.offline"
                            @load-ticket-details="loadTicketDetails"
                        />
                    </v-toolbar>
                    <table class="px-3 py-4">
                        <tr>
                            <td>Date:</td>
                            <td>{{ item.created }}</td>
                        </tr>
                        <tr>
                            <td>Rev:</td>
                            <td>{{ item.revision }}</td>
                        </tr>
                        <tr>
                            <td>Account:</td>
                            <td>{{ item.account }}</td>
                        </tr>
                        <tr>
                            <td>Code:</td>
                            <td>{{ item.member_code }}</td>
                        </tr>
                        <tr>
                            <td>Comments:</td>
                            <td>{{ item.comment }}</td>
                        </tr>
                        <tr>
                            <td>File Type:</td>
                            <td>{{ item.file_type }}</td>
                        </tr>
                    </table>
                </v-card>
            </div>
        </div>

        <v-col
            v-if="$vuetify.breakpoint.xs"
            class="d-flex flex-column justify-center align-center upload-btn-xs"
        >
            <v-btn
                class="text-subtitle-2"
                outlined
                light
                @click="showImageUploadDialog=true"
            >
                <v-icon>mdi-upload-outline</v-icon>
                Upload
            </v-btn>
        </v-col>

        <div
            v-if="!getTicketImages.length"
            class="d-flex flex-column justify-center align-center py-10"
            :style="{'height': $vuetify.breakpoint.xs ? 'calc(100vh - 100px)' : ''}"
        >
            <v-img
                src="../../assets/frame.svg"
                max-width="72"
                max-height="57"
            />
            <div class="my-4 no-img-text">
                There are no images attached to this ticket.
            </div>
            
            <v-btn
                v-if="!this.$vuetify.breakpoint.xs"
                class="text-subtitle-2"
                outlined
                light
                @click="showImageUploadDialog=true"
            >
                <v-icon>mdi-upload-outline</v-icon>
                Upload
            </v-btn>
        </div>
        <ImageUploadDialog
            :show="showImageUploadDialog"
            @closeImageUploadDialog="closeRespond"
        />
        <ImagePreviewDialog
            :preview-image="previewImage"
            :show="showImagePreviewDialog"
            @closeImagePreviewDialog="closePreviewRespond"
        />
        <v-dialog
            v-model="showOfflineImageUploadDialog"
            width="500"
            scrollable
            :fullscreen="this.$vuetify.breakpoint.xsOnly"
        >
            <UploadOfflineImagesCard
                id="upload-offline-dialog"
                @close-dialog="closeOfflineImageUploadDialog"
            />
        </v-dialog>
    </v-container>
</template>

<script>
import moment from 'moment';
import ImageUploadDialog from '../ImageUploadDialog.vue';
import ImagePreviewDialog from '../ImagePreviewDialog.vue';
import ImageDeleteDialog from './ImageDeleteDialog.vue';
import ImageEditDialog from './ImageEditDialog.vue';
import UploadOfflineImagesCard from '../Offline/UploadOfflineImagesCard.vue';
import serviceWorkerManager from '../../services/serviceWorkerManager';
import { guidEqual } from '@/utils/guid.js';

export default {
    name: 'Images',
    components: {
        ImageUploadDialog,
        ImagePreviewDialog,
        ImageDeleteDialog,
        ImageEditDialog,
        UploadOfflineImagesCard,
    },
    data: () => ({
        headers: [
            {
                text: 'Date',
                value: 'created',
                align: 'left',
                width: '150px',
                sortable: false,
            },
            {
                text: 'Rev',
                value: 'revision',
                align: 'left',
                width: '50px',
                sortable: false,
            },
            {
                text: 'Account',
                value: 'account',
                align: 'left',
                width: '100px',
                sortable: false,
            },
            {
                text: 'Code',
                value: 'member_code',
                align: 'left',
                width: '50px',
                sortable: false,
            },
            {
                text: 'Caption',
                value: 'caption',
                align: 'left',
                width: '130px',
                sortable: false,
            },
            {
                text: 'Comments',
                value: 'comment',
                align: 'left',
                width: '130px',
                sortable: false,
            },
            {
                text: 'File Type',
                value: 'file_type',
                align: 'left',
                width: '80px',
                sortable: false,
            },
            {
                text: 'View',
                value: 'view',
                align: 'center',
                width: '30px',
                sortable: false,
            },
            {
                text: 'Edit',
                value: 'edit',
                align: 'center',
                width: '30px',
                sortable: false,
            },
            {
                text: 'Delete',
                value: 'delete',
                align: 'center',
                width: '30px',
                sortable: false,
            },
        ],
        showImageUploadDialog: false,
        showImagePreviewDialog: false,
        selectedImages: [],
        ticketImages: [],
        checkedImages: [],
        previewImage: {},
        selectAll: false,
        showOfflineImageUploadDialog: false,
    }),
    computed: {
        onlineImageCount() {
            let count = 0;
            for(let image of this.getTicketImages) {
                if(!image.offline) {
                    ++count;
                }
            }
            return count;
        },
        offlineSelectAllDisabled() {
            return (this.offline && this.onlineImageCount > 0 );
        },
        offline() {
            return this.$store.getters.forceOffline;
        },
        selectedImagesName() {
            return this.selectedImages.map(image => {
                if (typeof image === 'string') {
                    const findImage = this.getTicketImages.find(item => guidEqual(item.rowid, image));
                    if (findImage) {
                        return findImage.caption;
                    }
                } else {
                    return image.caption;
                }
            });
        },
        getTicketDetails() {
            return this.$store.getters.ticketDetails;
        },
        getTicketImages() {
            if(this.getTicketDetails && this.getTicketDetails?.Ticket_Images) {
                return this.getTicketDetails.Ticket_Images.map((item) => {
                    item.created = moment(item.created).format(' DD MMM YYYY, hh:mm A');
                    item.selected = false;
                    return item;
                });
            } else {
                return [];
            }
        },
        offlineImageCount() {
            return this.getTicketDetails.Ticket_Images.filter(image => (!!image.offline)).length;
        },
    },
    methods: {
        itemRowColor(image) {
            if(image.offline) {
                return 'offline-image';
            }
        },
        closeRespond(isReloadTicketDetails) {
            this.showImageUploadDialog = false;
            if (isReloadTicketDetails) {
                this.$emit('reloadTicketDetails');
            }
        },
        closePreviewRespond() {
            this.showImagePreviewDialog = false;
        },
        showPreviewImage(ticketImage) {
            this.showImagePreviewDialog = true;
            this.previewImage = ticketImage;
        },
        handelSelectItem() {
            if(this.selectedImages.length === this.getTicketImages.length) {
                this.selectAll = true;
            } else {
                this.selectAll = false;
            }
        },
        selectAllItems() {
            if(this.selectAll) {
                this.selectedImages = this.getTicketImages.map(item => item.rowid);
            } else {
                this.selectedImages = [];
            }
        },
        loadTicketDetails() {
            this.$emit('reloadTicketDetails');
            this.selected = [];
            this.selectAll = false;
        },
        uploadOfflineImages() {
            this.showOfflineImageUploadDialog = true;
            serviceWorkerManager.postAllOfflineImages();
        },
        closeOfflineImageUploadDialog() {
            this.$emit('reloadTicketDetails');
            this.selected = [];
            this.selectAll = false;
            this.showOfflineImageUploadDialog=false;
        }
    }
};
</script>

<style>
    #main_images_table.v-data-table {
        border: 1px solid var(--ll-border);
        width: 100%;
        border-bottom-left-radius: 0px;
        border-bottom-right-radius: 0px;
        overflow: hidden;
    }
    #main_images_table.v-data-table__wrapper {
        border-radius: 4px;
    }
    #main_images_table .v-data-table-header th{
        background: var(--ll-surface-subtle) !important;
    }
    .ticket-images-container {
        display: flex;
        justify-content: space-between !important;
    }
    .ticket-images-container .upload-img-btn {
        padding-top: 0;
        padding-bottom: 0;
        margin-top: 14px;
    }
    .mobile-view-images {
        position: fixed;
        bottom: 0;
        z-index: 99;
        border-top: 1px solid var(--ll-border);
        padding: 10px;
        left: 0;
        right: 0;
        box-shadow: 0px 0px 6px 1px var(--ll-shadow);
    }
    .mobile-view-images button {
        width: 98%;
    }
    .mobile-view-selectall {
        display: flex;
        align-items: center;
        margin: 0;
        padding: 0px 25px;
    }

    #ticket-images-cards .v-card {
        padding-bottom: 16px;
        margin: 16px 0px;
    }
    #ticket-images-cards .ticket-card-header {
        background-color: var(--ll-surface-subtle);
    }
    #ticket-images-cards .ticket-card-ticket-revision a {
        text-decoration: none;
        color: var(--ll-link);
        font-size: 16px;
        font-weight: 500;
        line-height: 24px;
    }
    #ticket-images-cards .ticket-card-info {
        border-radius: 4px;
        border: 1px solid var(--ll-border-subtle);
        height: 28px;
        display: inline-block;
        min-width: 40px;
        margin-top: 16px;
        margin-bottom: 16px;
        margin-right: 8px;
        padding: 4px 8px;
        color: var(--ll-text-primary);
        font-size: 12px;
    }
    #ticket-images-cards table {
        font-size: 12px;
        width: 100%;
    }
    #ticket-images-cards table tr td:first-child {
        width: 82px;
    }
    #ticket-images-cards table tr td:nth-child(2) {
        font-weight: 500;
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
        max-width: 0;
    }
    #ticket-images-cards .status-open-chip {
        margin-right: 10px;
    }
    .checkbox-container .v-input--selection-controls {
        margin-top: 16px !important;
        padding-top: 4px !important;
    }
    .cp {
        cursor: pointer;
    }
    .no-img-text {
        color: var(--ll-text-secondary);
    }
    .image-cards {
        overflow-y: scroll;
        /* height: 76vh; */
    }
    .upload-btn-xs {
        position: fixed;
        bottom: 0;
        z-index: 99;
        background: var(--ll-surface);
        border-top: 1px solid var(--ll-border);
        padding: 10px;
        left: 0;
        right: 0;
        box-shadow: 0px 0px 6px 1px var(--ll-shadow);
    }
    .upload-btn-xs button {
        width: 98%;
    }
    .offline-image {
        background-color: var(--ll-surface-error);
    }
</style>
